import React, { useState } from 'react';
import { X, FolderPlus, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';

/**
 * Modal for creating new folders
 * @param {Function} onClose - Close modal callback
 * @param {Function} onSuccess - Success callback with new folder data
 * @param {number} parentId - Parent folder ID (null for root)
 * @param {string} parentName - Parent folder name for display
 * @param {string} phase - Current phase (onboarding/preboarding/admin)
 * @param {string} section - Optional section (company-wide, role-specific, etc.)
 */
const CreateFolderModal = ({
    onClose,
    onSuccess,
    parentId = null,
    parentName = null,
    phase: initialPhase,
    section = null,
    showPhaseSelector = false
}) => {
    const [folderName, setFolderName] = useState('');
    const [folderColor, setFolderColor] = useState('#6366f1');
    const [selectedPhase, setSelectedPhase] = useState(initialPhase || 'onboarding');
    const [loading, setLoading] = useState(false);

    // Use selectedPhase for operations when showPhaseSelector is true
    const phase = showPhaseSelector ? selectedPhase : initialPhase;

    const colors = [
        '#6366f1', // Indigo
        '#8b5cf6', // Violet
        '#ec4899', // Pink
        '#ef4444', // Red
        '#f97316', // Orange
        '#eab308', // Yellow
        '#22c55e', // Green
        '#14b8a6', // Teal
        '#3b82f6', // Blue
        '#64748b', // Slate
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!folderName.trim()) {
            toast.error('Please enter a folder name');
            return;
        }

        try {
            setLoading(true);
            console.log('Creating folder with:', {
                name: folderName.trim(),
                parent_id: parentId,
                phase,
                section,
                color: folderColor
            });

            const response = await api.post('/folders', {
                name: folderName.trim(),
                parent_id: parentId,
                phase,
                section,
                color: folderColor
            });

            if (response.data.folder) {
                toast.success('Folder created successfully');
                onSuccess?.(response.data.folder);
                onClose();
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            toast.error(error.response?.data?.message || 'Failed to create folder');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-xl">
                                <FolderPlus className="text-primary" size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Create Folder</h2>
                                {parentName && (
                                    <p className="text-sm text-gray-500">Inside: {parentName}</p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Phase Selector (for unified view) */}
                    {showPhaseSelector && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Lifecycle Stage
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { value: 'preboarding', label: 'Preboarding', color: '#f59e0b' },
                                    { value: 'onboarding', label: 'Onboarding', color: '#3b82f6' },
                                    { value: 'both', label: 'Both', color: '#8b5cf6' },
                                ].map(({ value, label, color }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setSelectedPhase(value)}
                                        className={`p-2 border-2 rounded-xl text-center transition-all text-sm font-medium ${
                                            selectedPhase === value
                                                ? 'border-current'
                                                : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                        }`}
                                        style={selectedPhase === value ? {
                                            borderColor: color,
                                            backgroundColor: color + '10',
                                            color: color
                                        } : {}}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Folder Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Folder Name
                        </label>
                        <input
                            type="text"
                            value={folderName}
                            onChange={(e) => setFolderName(e.target.value)}
                            placeholder="e.g., Company Policies"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                            autoFocus
                        />
                    </div>

                    {/* Color Picker */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Folder Color
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {colors.map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => setFolderColor(color)}
                                    className={`
                    w-8 h-8 rounded-full transition-all
                    ${folderColor === color
                                            ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
                                            : 'hover:scale-110'
                                        }
                  `}
                                    style={{ backgroundColor: color }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !folderName.trim()}
                            className="flex-1 px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary-hover transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <FolderPlus size={18} />
                                    Create Folder
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateFolderModal;
